/**
 * patternShapesSnapHelper.ts
 *
 * Shared snap logic for Pattern Shapes — used by both PatternShapesManipulative
 * (individual drop) and PatternShapesSelectionGroup (group drop).
 */
import { UNIT, BG_GRID, TRI_H, SHAPE_VERTICES, OUTLINE_VERTICES, SNAP_THRESHOLD, } from './patternShapesConstants';
import { getCanvasVertices, getMidpoints } from './PatternShapesManipulative';
/** Maximum allowed center movement from a single snap — prevents large jumps when
 *  SNAP_THRESHOLD is generous (30px) but the resulting delta would visually jerk the shape. */
var MAX_SNAP_DELTA = 15;
/**
 * Compute the snapped canvas position for a single pattern shape after a drag.
 *
 * Priority order (highest wins):
 *   1. Vertex-to-vertex snap against other shapes
 *   2. Midpoint-to-midpoint snap against other shapes
 *   3. Outline perimeter vertex snap
 *   4. Background grid snap
 *
 * @param rawX        Raw canvas X (top-left of shape bounds) after drag
 * @param rawY        Raw canvas Y (top-left of shape bounds) after drag
 * @param shapeType   Shape type of the shape being snapped
 * @param rotation    Current rotation in degrees
 * @param scale       Current scale factor
 * @param excludeIds  Shape IDs to skip during shape-to-shape snap (the dragged shapes)
 * @param manipulative  Full manipulative list from DataContext
 * @param bgPattern   Current background pattern ('none' | 'square' | 'triangle' | ...)
 */
export function computePatternShapeSnap(rawX, rawY, shapeType, rotation, scale, excludeIds, manipulative, bgPattern) {
    var _a;
    var localVerts = SHAPE_VERTICES[shapeType];
    var all = [];
    if (localVerts && localVerts.length > 0) {
        var currentVertCanvas_3 = getCanvasVertices(localVerts, { x: rawX, y: rawY }, rotation, scale || 1, shapeType);
        var currentMidCanvas_1 = getCanvasVertices(getMidpoints(localVerts), { x: rawX, y: rawY }, rotation, scale || 1, shapeType);
        var nC_1 = currentVertCanvas_3.length;
        // Shape-to-shape
        manipulative.forEach(function (item) {
            var _a, _b, _c, _d;
            if (item.type !== 'patternShapes')
                return;
            if (excludeIds.includes(item.data.id))
                return;
            var d = item.data;
            if (d.isOutline)
                return;
            if (d.groupId && excludeIds.includes(d.groupId))
                return;
            var otherType = d.shapeType;
            var otherLocalVerts = SHAPE_VERTICES[otherType];
            if (!otherLocalVerts || otherLocalVerts.length === 0)
                return;
            var otherVertCanvas = getCanvasVertices(otherLocalVerts, d.center, (_a = d.rotation) !== null && _a !== void 0 ? _a : 0, (_b = d.scale) !== null && _b !== void 0 ? _b : 1, otherType);
            var otherMidCanvas = getCanvasVertices(getMidpoints(otherLocalVerts), d.center, (_c = d.rotation) !== null && _c !== void 0 ? _c : 0, (_d = d.scale) !== null && _d !== void 0 ? _d : 1, otherType);
            var nO = otherVertCanvas.length;
            // Edge-to-edge (averaged offset) — score 4 (wins over single-vertex at equal distance)
            for (var i = 0; i < nC_1; i++) {
                var cv0 = currentVertCanvas_3[i];
                var cv1 = currentVertCanvas_3[(i + 1) % nC_1];
                for (var j = 0; j < nO; j++) {
                    for (var _i = 0, _e = [
                        [otherVertCanvas[j], otherVertCanvas[(j + 1) % nO]],
                        [otherVertCanvas[(j + 1) % nO], otherVertCanvas[j]],
                    ]; _i < _e.length; _i++) {
                        var _f = _e[_i], ta = _f[0], tb = _f[1];
                        var d0 = Math.hypot(cv0.x - ta.x, cv0.y - ta.y);
                        var d1 = Math.hypot(cv1.x - tb.x, cv1.y - tb.y);
                        if (d0 >= SNAP_THRESHOLD || d1 >= SNAP_THRESHOLD)
                            continue;
                        var ox = ((ta.x - cv0.x) + (tb.x - cv1.x)) / 2;
                        var oy = ((ta.y - cv0.y) + (tb.y - cv1.y)) / 2;
                        all.push({ ox: ox, oy: oy, dist: Math.hypot(ox, oy), score: 4 });
                    }
                }
            }
            // Single vertex — score 3 (shape)
            for (var _g = 0, currentVertCanvas_4 = currentVertCanvas_3; _g < currentVertCanvas_4.length; _g++) {
                var cv = currentVertCanvas_4[_g];
                for (var _h = 0, otherVertCanvas_1 = otherVertCanvas; _h < otherVertCanvas_1.length; _h++) {
                    var ov = otherVertCanvas_1[_h];
                    var vdist = Math.hypot(cv.x - ov.x, cv.y - ov.y);
                    if (vdist >= SNAP_THRESHOLD)
                        continue;
                    var ox = ov.x - cv.x;
                    var oy = ov.y - cv.y;
                    all.push({ ox: ox, oy: oy, dist: Math.hypot(ox, oy), score: 3 });
                }
            }
            // Midpoint — score 3 (shape)
            for (var _j = 0, currentMidCanvas_2 = currentMidCanvas_1; _j < currentMidCanvas_2.length; _j++) {
                var cv = currentMidCanvas_2[_j];
                for (var _k = 0, otherMidCanvas_1 = otherMidCanvas; _k < otherMidCanvas_1.length; _k++) {
                    var ov = otherMidCanvas_1[_k];
                    var vdist = Math.hypot(cv.x - ov.x, cv.y - ov.y);
                    if (vdist >= SNAP_THRESHOLD)
                        continue;
                    var ox = ov.x - cv.x;
                    var oy = ov.y - cv.y;
                    all.push({ ox: ox, oy: oy, dist: Math.hypot(ox, oy), score: 3 });
                }
            }
        });
        var _loop_1 = function (item) {
            if (item.type !== 'patternShapes')
                return "continue";
            var d = item.data;
            if (!d.isOutline)
                return "continue";
            var pathStr = (_a = d.path) !== null && _a !== void 0 ? _a : '';
            var idMatch = pathStr.match(/Shape_(\d+)\.svg/i);
            if (!idMatch)
                return "continue";
            var outlineId = parseInt(idMatch[1], 10);
            var outlinePerimVerts = OUTLINE_VERTICES[outlineId];
            if (!outlinePerimVerts || outlinePerimVerts.length === 0)
                return "continue";
            var ox = d.center.x;
            var oy = d.center.y;
            var outlineCanvasVerts = outlinePerimVerts.map(function (v) { return ({ x: ox + v.x, y: oy + v.y }); });
            for (var _e = 0, currentVertCanvas_5 = currentVertCanvas_3; _e < currentVertCanvas_5.length; _e++) {
                var cv = currentVertCanvas_5[_e];
                for (var _f = 0, outlineCanvasVerts_1 = outlineCanvasVerts; _f < outlineCanvasVerts_1.length; _f++) {
                    var ov = outlineCanvasVerts_1[_f];
                    var vdist = Math.hypot(cv.x - ov.x, cv.y - ov.y);
                    if (vdist >= SNAP_THRESHOLD)
                        continue;
                    var ox_1 = ov.x - cv.x;
                    var oy_1 = ov.y - cv.y;
                    all.push({ ox: ox_1, oy: oy_1, dist: Math.hypot(ox_1, oy_1), score: 2 });
                }
            }
        };
        // Outline perimeter — score 2
        for (var _i = 0, manipulative_1 = manipulative; _i < manipulative_1.length; _i++) {
            var item = manipulative_1[_i];
            _loop_1(item);
        }
        // Grid snap — score 1
        try {
            if (bgPattern && bgPattern !== 'none') {
                var gox = 0;
                var goy = 0;
                if (bgPattern === 'triangle') {
                    var HALF = BG_GRID / 2;
                    var bestDist = Infinity;
                    for (var _b = 0, currentVertCanvas_1 = currentVertCanvas_3; _b < currentVertCanvas_1.length; _b++) {
                        var cv = currentVertCanvas_1[_b];
                        var row = Math.round(cv.y / TRI_H);
                        var snapY = row * TRI_H;
                        var xBase = row % 2 !== 0 ? HALF : 0;
                        var snapX = Math.round((cv.x - xBase) / BG_GRID) * BG_GRID + xBase;
                        var dist = Math.hypot(snapX - cv.x, snapY - cv.y);
                        if (dist < bestDist) {
                            bestDist = dist;
                            gox = snapX - cv.x;
                            goy = snapY - cv.y;
                        }
                    }
                }
                else {
                    var bestXDist = Infinity;
                    var bestYDist = Infinity;
                    for (var _c = 0, currentVertCanvas_2 = currentVertCanvas_3; _c < currentVertCanvas_2.length; _c++) {
                        var cv = currentVertCanvas_2[_c];
                        var sx = Math.round(cv.x / BG_GRID) * BG_GRID;
                        var dx = Math.abs(sx - cv.x);
                        if (dx < bestXDist) {
                            bestXDist = dx;
                            gox = sx - cv.x;
                        }
                        var sy = Math.round(cv.y / BG_GRID) * BG_GRID;
                        var dy = Math.abs(sy - cv.y);
                        if (dy < bestYDist) {
                            bestYDist = dy;
                            goy = sy - cv.y;
                        }
                    }
                }
                var gridDist = Math.hypot(gox, goy);
                if (gridDist > 0)
                    all.push({ ox: gox, oy: goy, dist: gridDist, score: 1 });
            }
        }
        catch (_d) {
            var gox = Math.round(rawX / UNIT) * UNIT - rawX;
            var goy = Math.round(rawY / UNIT) * UNIT - rawY;
            all.push({ ox: gox, oy: goy, dist: Math.hypot(gox, goy), score: 1 });
        }
    }
    else if (bgPattern && bgPattern !== 'none') {
        var gox = Math.round(rawX / BG_GRID) * BG_GRID - rawX;
        var goy = Math.round(rawY / BG_GRID) * BG_GRID - rawY;
        var gridDist = Math.hypot(gox, goy);
        if (gridDist > 0)
            all.push({ ox: gox, oy: goy, dist: gridDist, score: 1 });
    }
    if (all.length > 0) {
        // Nearest wins; score (edge-to-edge=4 > shape=3 > outline=2 > grid=1) breaks ties at equal distance
        all.sort(function (a, b) { return a.dist - b.dist || b.score - a.score; });
        var best = all[0];
        // Cap: only apply snap if the resulting center movement is within MAX_SNAP_DELTA
        if (Math.hypot(best.ox, best.oy) <= MAX_SNAP_DELTA) {
            return { x: rawX + best.ox, y: rawY + best.oy, snapScore: best.score };
        }
    }
    return { x: rawX, y: rawY, snapScore: 0 };
}
/**
 * Compute a single uniform snap delta for a GROUP of shapes.
 *
 * Tries every shape in the group as a snap candidate. The candidate that
 * produces the smallest snap distance wins and its (dx, dy) is applied to
 * all shapes in the group so they move together.
 *
 * If no vertex/outline snap fires for any shape, falls back to grid-snapping
 * the first shape and using that delta uniformly.
 */
export function computeGroupSnapDelta(shapes, allExcludeIds, manipulative, bgPattern) {
    var _a, _b, _c, _d, _e;
    if (shapes.length === 0)
        return { dx: 0, dy: 0 };
    var localVerts = function (shapeType) { return SHAPE_VERTICES[shapeType]; };
    var candidates = [];
    // ── Vertex-to-vertex (with edge-to-edge priority) ──
    for (var _i = 0, shapes_1 = shapes; _i < shapes_1.length; _i++) {
        var shape = shapes_1[_i];
        var verts = localVerts(shape.shapeType);
        if (!verts || verts.length === 0)
            continue;
        var nC = verts.length;
        var currentVerts = getCanvasVertices(verts, shape.center, shape.rotation, shape.scale || 1, shape.shapeType);
        var currentMids = getCanvasVertices(getMidpoints(verts), shape.center, shape.rotation, shape.scale || 1, shape.shapeType);
        for (var _f = 0, manipulative_2 = manipulative; _f < manipulative_2.length; _f++) {
            var item = manipulative_2[_f];
            if (item.type !== 'patternShapes')
                continue;
            if (allExcludeIds.includes(item.data.id))
                continue;
            var d = item.data;
            if (d.isOutline)
                continue;
            // Allow shapes from other groups as snap targets; only skip shapes in the same excluded group
            if (d.groupId && allExcludeIds.includes(d.groupId))
                continue;
            var otherType = d.shapeType;
            var otherVerts = localVerts(otherType);
            if (!otherVerts || otherVerts.length === 0)
                continue;
            var nO = otherVerts.length;
            var otherCanvasVerts = getCanvasVertices(otherVerts, d.center, (_a = d.rotation) !== null && _a !== void 0 ? _a : 0, (_b = d.scale) !== null && _b !== void 0 ? _b : 1, otherType);
            var otherMids = getCanvasVertices(getMidpoints(otherVerts), d.center, (_c = d.rotation) !== null && _c !== void 0 ? _c : 0, (_d = d.scale) !== null && _d !== void 0 ? _d : 1, otherType);
            // Edge-to-edge — score 4 (wins over single-vertex at equal distance)
            for (var i = 0; i < nC; i++) {
                var cv0 = currentVerts[i];
                var cv1 = currentVerts[(i + 1) % nC];
                for (var j = 0; j < nO; j++) {
                    for (var _g = 0, _h = [
                        [otherCanvasVerts[j], otherCanvasVerts[(j + 1) % nO]],
                        [otherCanvasVerts[(j + 1) % nO], otherCanvasVerts[j]],
                    ]; _g < _h.length; _g++) {
                        var _j = _h[_g], ta = _j[0], tb = _j[1];
                        var d0 = Math.hypot(cv0.x - ta.x, cv0.y - ta.y);
                        var d1 = Math.hypot(cv1.x - tb.x, cv1.y - tb.y);
                        if (d0 >= SNAP_THRESHOLD || d1 >= SNAP_THRESHOLD)
                            continue;
                        var cdx = ((ta.x - cv0.x) + (tb.x - cv1.x)) / 2;
                        var cdy = ((ta.y - cv0.y) + (tb.y - cv1.y)) / 2;
                        candidates.push({ dx: cdx, dy: cdy, score: 4, dist: Math.hypot(cdx, cdy) });
                    }
                }
            }
            // Single vertex — score 3 (shape)
            for (var _k = 0, currentVerts_1 = currentVerts; _k < currentVerts_1.length; _k++) {
                var cv = currentVerts_1[_k];
                for (var _l = 0, otherCanvasVerts_1 = otherCanvasVerts; _l < otherCanvasVerts_1.length; _l++) {
                    var ov = otherCanvasVerts_1[_l];
                    var vdist = Math.hypot(cv.x - ov.x, cv.y - ov.y);
                    if (vdist >= SNAP_THRESHOLD)
                        continue;
                    var dx = ov.x - cv.x;
                    var dy = ov.y - cv.y;
                    candidates.push({ dx: dx, dy: dy, score: 3, dist: Math.hypot(dx, dy) });
                }
            }
            // Midpoint — score 3 (shape)
            for (var _m = 0, currentMids_1 = currentMids; _m < currentMids_1.length; _m++) {
                var cv = currentMids_1[_m];
                for (var _o = 0, otherMids_1 = otherMids; _o < otherMids_1.length; _o++) {
                    var ov = otherMids_1[_o];
                    var vdist = Math.hypot(cv.x - ov.x, cv.y - ov.y);
                    if (vdist >= SNAP_THRESHOLD)
                        continue;
                    var dx = ov.x - cv.x;
                    var dy = ov.y - cv.y;
                    candidates.push({ dx: dx, dy: dy, score: 3, dist: Math.hypot(dx, dy) });
                }
            }
        }
    }
    // ── Outline perimeter snap — score 2 ──
    for (var _p = 0, shapes_2 = shapes; _p < shapes_2.length; _p++) {
        var shape = shapes_2[_p];
        var verts = localVerts(shape.shapeType);
        if (!verts || verts.length === 0)
            continue;
        var canvasVerts = getCanvasVertices(verts, shape.center, shape.rotation, shape.scale || 1, shape.shapeType);
        var _loop_2 = function (item) {
            if (item.type !== 'patternShapes')
                return "continue";
            var d = item.data;
            if (!d.isOutline)
                return "continue";
            var pathStr = (_e = d.path) !== null && _e !== void 0 ? _e : '';
            var idMatch = pathStr.match(/Shape_(\d+)\.svg/i);
            if (!idMatch)
                return "continue";
            var outlineId = parseInt(idMatch[1], 10);
            var outlinePerimVerts = OUTLINE_VERTICES[outlineId];
            if (!outlinePerimVerts || outlinePerimVerts.length === 0)
                return "continue";
            var ox = d.center.x;
            var oy = d.center.y;
            var outlineCanvasVerts = outlinePerimVerts.map(function (v) { return ({
                x: ox + v.x,
                y: oy + v.y,
            }); });
            for (var _u = 0, canvasVerts_3 = canvasVerts; _u < canvasVerts_3.length; _u++) {
                var cv = canvasVerts_3[_u];
                for (var _v = 0, outlineCanvasVerts_2 = outlineCanvasVerts; _v < outlineCanvasVerts_2.length; _v++) {
                    var ov = outlineCanvasVerts_2[_v];
                    var vdist = Math.hypot(cv.x - ov.x, cv.y - ov.y);
                    if (vdist >= SNAP_THRESHOLD)
                        continue;
                    var dx = ov.x - cv.x;
                    var dy = ov.y - cv.y;
                    candidates.push({ dx: dx, dy: dy, score: 2, dist: Math.hypot(dx, dy) });
                }
            }
        };
        for (var _q = 0, manipulative_3 = manipulative; _q < manipulative_3.length; _q++) {
            var item = manipulative_3[_q];
            _loop_2(item);
        }
    }
    // ── Grid snap — score 1 ──
    if (bgPattern && bgPattern !== 'none') {
        for (var _r = 0, shapes_3 = shapes; _r < shapes_3.length; _r++) {
            var shape = shapes_3[_r];
            var verts = localVerts(shape.shapeType);
            var canvasVerts = verts && verts.length > 0
                ? getCanvasVertices(verts, shape.center, shape.rotation, shape.scale || 1, shape.shapeType)
                : [{ x: shape.center.x, y: shape.center.y }];
            var dx = 0;
            var dy = 0;
            if (bgPattern === 'triangle') {
                var HALF = BG_GRID / 2;
                var bestDist = Infinity;
                for (var _s = 0, canvasVerts_1 = canvasVerts; _s < canvasVerts_1.length; _s++) {
                    var cv = canvasVerts_1[_s];
                    var row = Math.round(cv.y / TRI_H);
                    var snapY = row * TRI_H;
                    var xBase = row % 2 !== 0 ? HALF : 0;
                    var snapX = Math.round((cv.x - xBase) / BG_GRID) * BG_GRID + xBase;
                    var dist_1 = Math.hypot(snapX - cv.x, snapY - cv.y);
                    if (dist_1 < bestDist) {
                        bestDist = dist_1;
                        dx = snapX - cv.x;
                        dy = snapY - cv.y;
                    }
                }
            }
            else {
                var bestXDist = Infinity;
                var bestYDist = Infinity;
                for (var _t = 0, canvasVerts_2 = canvasVerts; _t < canvasVerts_2.length; _t++) {
                    var cv = canvasVerts_2[_t];
                    var sx = Math.round(cv.x / BG_GRID) * BG_GRID;
                    var distX = Math.abs(sx - cv.x);
                    if (distX < bestXDist) {
                        bestXDist = distX;
                        dx = sx - cv.x;
                    }
                    var sy = Math.round(cv.y / BG_GRID) * BG_GRID;
                    var distY = Math.abs(sy - cv.y);
                    if (distY < bestYDist) {
                        bestYDist = distY;
                        dy = sy - cv.y;
                    }
                }
            }
            var dist = Math.hypot(dx, dy);
            if (dist > 0)
                candidates.push({ dx: dx, dy: dy, score: 1, dist: dist });
        }
    }
    if (candidates.length > 0) {
        // Nearest wins; score (edge-to-edge=4 > shape=3 > outline=2 > grid=1) breaks ties at equal distance
        candidates.sort(function (a, b) { return a.dist - b.dist || b.score - a.score; });
        var best = candidates[0];
        if (Math.hypot(best.dx, best.dy) <= MAX_SNAP_DELTA) {
            return { dx: best.dx, dy: best.dy };
        }
    }
    return { dx: 0, dy: 0 };
}
